// Generates a stable, deterministic roster of 110 drivers per shift.
// Names come from a small pool; numbering keeps them unique even when
// the pool repeats. Seeds are baked so the same names show up across
// renders / page reloads — no surprises during the pitch.

const FIRST_NAMES = [
  'Herb', 'Mike', 'Carlos', 'Tina', 'Dave', 'Ron', 'Lisa', 'Pete', 'Kira', 'Sam',
  'Jamal', 'Rosa', 'Brett', 'Maria', 'Tony', 'Aisha', 'Mark', 'Jen', 'Diego', 'Pat',
  'Trevor', 'Wes', 'Fae', 'Cam', 'Eli', 'Bri', 'Nora', 'Owen', 'Ruth', 'Theo',
  'Dane', 'Sage', 'Rex', 'Jules', 'Vic', 'Kaz', 'Lin', 'Mo', 'Quin', 'Roy',
  'Stevie', 'Tim', 'Uma', 'Val', 'Will', 'Xavi', 'Yara', 'Zane', 'Ana', 'Ben',
  'Cody', 'Dee', 'Eric', 'Fran', 'Gabe', 'Hank', 'Ivan', 'Jada', 'Kelly', 'Lou',
  'Maggie', 'Nash', 'Otis', 'Paige', 'Quincy', 'Reese',
];

const LAST_NAMES = [
  'Ballenger', 'Reyes', 'Nguyen', 'Patel', 'Garcia', 'Wright', 'Fernandez', 'Holt',
  'Singh', 'Brennan', 'Cole', 'Diaz', 'Ellis', 'Foster', 'Galloway', 'Hayes',
  'Iverson', 'Jensen', 'Khan', 'Levin', 'Marsh', 'Norris', 'Ortega', 'Park',
  'Quinn', 'Ramos', 'Sato', 'Tate', 'Underwood', 'Vargas', 'Walsh', 'Yates',
  'Acosta', 'Boone', 'Crawford', 'Donnelly', 'Estrada', 'Fitzgerald', 'Goldstein',
  'Harper', 'Ibarra', 'Jacobs', 'Kowalski', 'Lambert', 'Mendoza', 'Newton',
  'O’Brien', 'Pereira', 'Robles', 'Salazar',
];

// Shift codes: "X-Yz" where Y - X = 8 (eight-hour shift) and the
// suffix encodes the minute offset for both start and end:
//   a = :15, b = :30, c = :45
// e.g. "5-13b" = 5:30 → 13:30 (8 hours).
const SHIFT_CODES = {
  AM:  ['4-12a', '4-12b', '5-13a', '5-13b', '5-13c', '6-14a', '6-14b', '6-14c', '7-15a', '7-15b', '8-16a'],
  MID: ['10-18a', '10-18b', '11-19a', '11-19b', '12-20a', '13-21a'],
  PM:  ['14-22a', '15-23a', '15-23b', '16-24a', '17-01a', '18-02a'],
};

const STATUSES = [
  { code: 'on_shift',  label: 'On Shift',  weight: 70 },
  { code: 'on_break',  label: 'On Break',  weight: 8 },
  { code: 'on_lunch',  label: 'On Lunch',  weight: 8 },
  { code: 'in_run',    label: 'In Run',    weight: 14 },
];

function pickWeighted(rng, items) {
  const total = items.reduce((sum, it) => sum + it.weight, 0);
  let r = rng() * total;
  for (const it of items) {
    r -= it.weight;
    if (r <= 0) return it;
  }
  return items[items.length - 1];
}

// Deterministic pseudo-random (mulberry32). Seeded so reload doesn't shuffle.
function makeRng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildShiftDrivers(shift, count, baseBadge, seed) {
  const rng = makeRng(seed);
  const shiftCodes = SHIFT_CODES[shift];
  const drivers = [];
  // Assign deterministic seniority numbers (3-digit). Lower = senior.
  const seniorityPool = [];
  for (let i = 100; i <= 999; i++) seniorityPool.push(i);
  // Shuffle deterministically
  for (let i = seniorityPool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [seniorityPool[i], seniorityPool[j]] = [seniorityPool[j], seniorityPool[i]];
  }

  for (let i = 0; i < count; i++) {
    const first = FIRST_NAMES[i % FIRST_NAMES.length];
    // Stagger last names independently so we don't get name collisions
    const last = LAST_NAMES[(i * 7 + Math.floor(i / FIRST_NAMES.length) * 13) % LAST_NAMES.length];
    const status = pickWeighted(rng, STATUSES).code;
    const seniority = seniorityPool[i];
    drivers.push({
      id: `${shift.toLowerCase()}-${i + 1}`,
      badge: baseBadge + i,
      seniority,
      firstName: first,
      lastName: last,
      displayName: `${first} ${last}`,
      shift,
      shiftCode: shiftCodes[i % shiftCodes.length],
      status,
      tug: 28000 + ((seed * 17 + i * 23) % 9000),
    });
  }
  return drivers;
}

export const ROSTER = {
  AM:  buildShiftDrivers('AM',  110, 80000, 1),
  MID: buildShiftDrivers('MID', 110, 81000, 2),
  PM:  buildShiftDrivers('PM',  110, 82000, 3),
};

// Flat list with shift-aware filtering helpers.
export function allDrivers() {
  return [...ROSTER.AM, ...ROSTER.MID, ...ROSTER.PM];
}
export function driversForShift(shift) {
  return ROSTER[shift] || [];
}
export function findDriverById(id) {
  return allDrivers().find(d => d.id === id);
}
